import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { CampaignChannelKind, CampaignTestConversation } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfirm } from '@/contexts/confirmContext';
import { contactAgentApi } from '../api/contactAgentApi';
import { campaignTestConversationApi, messagingAdminApi } from '../api/messagingApi';
import { contactAgentError } from '../api/contactAgentScope';
import { whatsappAdminApi } from '@/features/whatsapp/api/whatsappApi';

export type TestSendVariableField = { key: string; label: string };

export type CampaignTestSendPanelProps = {
  actorRole: string;
  channelKind: CampaignChannelKind;
  campaignId?: string;
  agentId?: string;
  whatsAppChannelId?: string;
  messagingChannelId?: string;
  tenantId?: string;
  templateId?: string;
  templateReadOnly?: boolean;
  templates?: Array<{ id: string; name: string }>;
  variableKeys?: TestSendVariableField[];
  collapsed?: boolean;
};

function isValidPhone(value: string): boolean {
  const bounded = value.slice(0, 32).trim();
  if (!bounded.startsWith('+') || bounded.length < 9 || bounded.length > 17) {
    return false;
  }
  for (let i = 1; i < bounded.length; i += 1) {
    const code = bounded.charCodeAt(i);
    if (code < 48 || code > 57) {
      return false;
    }
  }
  return true;
}

function isValidEmail(value: string): boolean {
  const bounded = value.slice(0, 255).trim();
  const at = bounded.indexOf('@');
  if (at < 1 || at !== bounded.lastIndexOf('@')) {
    return false;
  }
  const domain = bounded.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  return dot > 0 && dot < domain.length - 1 && !domain.includes(' ');
}

function destinationValid(kind: CampaignChannelKind, to: string): boolean {
  return kind === 'EMAIL' ? isValidEmail(to) : isValidPhone(to);
}

export function CampaignTestSendPanel(props: CampaignTestSendPanelProps) {
  const { confirm } = useConfirm();
  const [to, setTo] = useState('');
  const [templateId, setTemplateId] = useState(props.templateId ?? '');
  const [openConversation, setOpenConversation] = useState(false);
  const [markAsTest, setMarkAsTest] = useState(true);
  const [subjectOverride, setSubjectOverride] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [threads, setThreads] = useState<CampaignTestConversation[]>([]);

  const isWhatsApp = props.channelKind === 'WHATSAPP';
  const listChannelId = props.whatsAppChannelId;

  useEffect(() => {
    setTemplateId(props.templateId ?? '');
  }, [props.templateId]);

  useEffect(() => {
    if (!listChannelId || props.actorRole === 'USER') {
      setThreads([]);
      return;
    }
    let cancelled = false;
    void campaignTestConversationApi
      .listWhatsApp(listChannelId, props.tenantId)
      .then(page => {
        if (!cancelled) {
          setThreads(page.items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setThreads([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listChannelId, props.actorRole, props.tenantId]);

  const requiredOk = useMemo(() => {
    if (!destinationValid(props.channelKind, to)) {
      return false;
    }
    if (isWhatsApp && !templateId.trim()) {
      return false;
    }
    return (props.variableKeys ?? []).every(field => (values[field.key] ?? '').trim().length > 0);
  }, [isWhatsApp, props.channelKind, props.variableKeys, templateId, to, values]);

  if (props.actorRole === 'USER') {
    return null;
  }

  const sendTest = async () => {
    if (!requiredOk) {
      return;
    }
    setSending(true);
    try {
      const payload = {
        to: to.slice(0, 255),
        channelKind: props.channelKind,
        templateId: templateId.slice(0, 128) || undefined,
        variables: values,
        openConversation: isWhatsApp ? openConversation : false,
        markAsTest: isWhatsApp ? undefined : markAsTest,
        subjectOverride: props.channelKind === 'EMAIL' ? subjectOverride.slice(0, 255) || undefined : undefined,
      };
      const result = await dispatchTestSend(props, payload);
      toast.success('Prueba enviada');
      if (result.conversationId && listChannelId) {
        const page = await campaignTestConversationApi.listWhatsApp(listChannelId, props.tenantId);
        setThreads(page.items);
      }
    } catch (error) {
      const message = contactAgentError(error);
      toast.error(message.includes('Limite') ? 'Esperá un minuto para otra prueba.' : message);
    } finally {
      setSending(false);
    }
  };

  const removeThread = async (conversationId: string) => {
    const ok = await confirm({
      title: 'Eliminar hilo de prueba',
      message: 'Se borra este hilo. La cartera no se toca.',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) {
      return;
    }
    try {
      if (listChannelId) {
        await campaignTestConversationApi.deleteWhatsApp(listChannelId, conversationId, props.tenantId);
      } else {
        await contactAgentApi.deleteTestConversation(conversationId);
      }
      setThreads(current => current.filter(item => item.id !== conversationId));
      toast.success('Hilo de prueba borrado');
    } catch (error) {
      toast.error(contactAgentError(error));
    }
  };

  const body = (
    <Card className='rounded-xl'>
      {props.collapsed ? null : (
        <CardHeader>
          <CardTitle>Probar este canal</CardTitle>
          <p className='text-sm text-muted-foreground'>
            Escribí el destino y los datos de esta prueba. No se usa la cartera. Si abrís conversación,
            después podés borrar el hilo.
          </p>
        </CardHeader>
      )}
      <CardContent className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='campaign-test-to'>{isWhatsApp || props.channelKind === 'SMS' ? 'Teléfono' : 'Para'}</Label>
          <Input
            id='campaign-test-to'
            autoComplete='off'
            value={to}
            maxLength={255}
            placeholder={props.channelKind === 'EMAIL' ? 'nombre@dominio.com' : '+54911…'}
            onChange={event => setTo(event.target.value.slice(0, 255))}
          />
        </div>

        {props.channelKind === 'EMAIL' ? (
          <div className='space-y-2'>
            <Label htmlFor='campaign-test-subject'>Asunto (opcional)</Label>
            <Input
              id='campaign-test-subject'
              autoComplete='off'
              value={subjectOverride}
              maxLength={255}
              onChange={event => setSubjectOverride(event.target.value.slice(0, 255))}
            />
          </div>
        ) : null}

        <TemplatePicker
          templateId={templateId}
          readOnly={Boolean(props.templateReadOnly)}
          templates={props.templates}
          required={isWhatsApp}
          onChange={setTemplateId}
        />

        {(props.variableKeys ?? []).map(field => (
          <div key={field.key} className='space-y-2'>
            <Label htmlFor={`campaign-test-var-${field.key}`}>{field.label}</Label>
            <Input
              id={`campaign-test-var-${field.key}`}
              autoComplete='off'
              maxLength={200}
              value={values[field.key] ?? ''}
              onChange={event =>
                setValues(current => ({ ...current, [field.key]: event.target.value.slice(0, 200) }))
              }
            />
          </div>
        ))}

        {to ? (
          <div className='rounded-xl border bg-muted/40 p-3 text-sm'>
            <p className='font-medium'>Vista previa</p>
            <p className='text-muted-foreground'>
              {props.channelKind === 'EMAIL' ? 'Mail' : props.channelKind === 'SMS' ? 'SMS' : 'WhatsApp'} a {to}
            </p>
          </div>
        ) : null}

        {isWhatsApp ? (
          <label className='flex items-start gap-2 text-sm'>
            <input
              type='checkbox'
              checked={openConversation}
              onChange={event => setOpenConversation(event.target.checked)}
            />
            <span>
              Abrir conversación de prueba
              <span className='block text-xs text-muted-foreground'>
                El destinatario puede responder en el bot. Después podés borrar el hilo.
              </span>
            </span>
          </label>
        ) : (
          <label className='flex items-start gap-2 text-sm'>
            <input
              type='checkbox'
              checked={markAsTest}
              onChange={event => setMarkAsTest(event.target.checked)}
            />
            <span>Marcar como prueba</span>
          </label>
        )}

        <Button type='button' disabled={!requiredOk || sending} onClick={() => void sendTest()}>
          {sending ? 'Enviando…' : 'Enviar prueba'}
        </Button>

        {isWhatsApp ? (
          <TestThreadList items={threads} onDelete={id => void removeThread(id)} />
        ) : null}
      </CardContent>
    </Card>
  );

  if (!props.collapsed) {
    return body;
  }

  return (
    <details className='rounded-xl border'>
      <summary className='cursor-pointer px-4 py-3 text-sm font-medium'>Probar con un destino</summary>
      <div className='border-t'>{body}</div>
    </details>
  );
}

function TemplatePicker({
  templateId,
  readOnly,
  templates,
  required,
  onChange,
}: {
  templateId: string;
  readOnly: boolean;
  templates?: Array<{ id: string; name: string }>;
  required: boolean;
  onChange: (value: string) => void;
}) {
  if (readOnly) {
    return templateId ? (
      <p className='text-sm text-muted-foreground'>Plantilla: {templateId}</p>
    ) : (
      <p className='text-sm text-muted-foreground'>Esta campaña todavía no tiene plantilla.</p>
    );
  }
  if (!templates?.length) {
    return required ? (
      <p className='text-sm text-muted-foreground'>Elegí una plantilla de WhatsApp para probar.</p>
    ) : null;
  }
  return (
    <div className='space-y-2'>
      <Label htmlFor='campaign-test-template'>Plantilla</Label>
      <select
        id='campaign-test-template'
        className='w-full rounded-md border border-input bg-background p-2 text-sm'
        value={templateId}
        onChange={event => onChange(event.target.value)}
      >
        <option value=''>Elegí una plantilla</option>
        {templates.map(item => (
          <option key={item.id} value={item.name || item.id}>
            {item.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function TestThreadList({
  items,
  onDelete,
}: {
  items: CampaignTestConversation[];
  onDelete: (id: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>Todavía no hay conversaciones de prueba en este canal.</p>
    );
  }
  return (
    <div className='space-y-2'>
      <p className='text-sm font-medium'>Conversaciones de prueba de este canal</p>
      <ul className='space-y-2'>
        {items.map(item => (
          <li key={item.id} className='flex items-center justify-between rounded-lg border px-3 py-2 text-sm'>
            <span>
              {item.phoneMasked} · {item.startedAt.slice(0, 16).replace('T', ' ')}
              <Badge className='ml-2' variant='secondary'>
                Prueba
              </Badge>
            </span>
            <Button
              type='button'
              variant='destructive'
              size='sm'
              aria-label='Eliminar hilo de prueba'
              onClick={() => onDelete(item.id)}
            >
              Eliminar
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

async function dispatchTestSend(
  props: CampaignTestSendPanelProps,
  payload: Parameters<typeof contactAgentApi.testSend>[1],
) {
  if (props.campaignId) {
    return contactAgentApi.testSend(props.campaignId, payload);
  }
  if (props.whatsAppChannelId) {
    return whatsappAdminApi.testSend(props.whatsAppChannelId, payload, props.tenantId);
  }
  if (props.messagingChannelId) {
    return messagingAdminApi.testSend(props.messagingChannelId, payload, props.tenantId);
  }
  if (props.channelKind === 'EMAIL') {
    return contactAgentApi.testSendDraft({
      ...payload,
      ...(props.agentId ? { agentId: props.agentId } : {}),
    });
  }
  throw new Error('Canal de prueba no configurado');
}
